import type {ReactElement} from 'react';
import React from 'react';
import {Dropdown} from '@Components/dropdown-v2';
import {IconType} from '@Components/icon-v2/icon.types';
import {Type} from '@Components/button';
import {NavRightSideOptionTypes} from '@Components/poster-editor/components/poster-editor-nav-bar/components/nav-right-options/nav-right-options.types';
import {useAppSelector} from '@/hooks';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import styles from './nav-download-option.module.scss';
import {DownloadPanel} from '@Components/download-panel/download-panel';
import BaseNavOption from '@Components/poster-editor/components/poster-editor-nav-bar/components/base-nav-option/base-nav-option';
import {onOpenDownloadModal} from '@Components/poster-editor/library/poster-editor-open-modals';
import {isEditorMobileVariant} from '@Components/poster-editor/library/poster-editor-library';
import {openNewDownloadModal} from '@Modals/download-modal';
import {DownloadStateTypes} from '@Components/download-panel/download-panel.reducer';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {isPremiumUser} from '@Utils/user.util';

interface NavDownloadOptionProps {
  downloadOptionType?: NavRightSideOptionTypes;
}

export function NavDownloadOption({downloadOptionType = NavRightSideOptionTypes.BUTTON}: NavDownloadOptionProps): ReactElement {
  const isInitialized = useAppSelector((state) => {
    return state.posterEditor.posterObject?.isInitialized;
  });
  const posterObject = useAppSelector((state) => {
    return state.posterEditor.posterObject;
  });
  const currentProcessingOrderId = useAppSelector((state) => {
    return state.downloadDialog.currentProcessingOrderId;
  });
  const isFileDropdownOpen = useAppSelector((state) => {
    return state.downloadDialog.isFileDropdownOpen;
  });
  const currentDownloadData = useAppSelector((state) => {
    return state.downloadDialog.orderStateHashmap[currentProcessingOrderId ?? ''];
  });

  const isSliderBeingDragged = useAppSelector((state) => {
    return state.downloadDialog.isSliderBeingDragged;
  });

  if (!posterObject) {
    throw new Error('posterObject is not defined');
  }

  const isDownloading = (): boolean => {
    if (!currentDownloadData) {
      return false;
    }
    return !(currentDownloadData && currentDownloadData.state !== DownloadStateTypes.DOWNLOADING);
  };

  const getProgress = (): ReactElement | null => {
    if (!isDownloading()) {
      return null;
    }
    return (
      <div
        style={{
          width: `${Math.round(currentDownloadData.displayProgress ?? 0)}%`,
        }}
        className={styles.progress}
      />
    );
  };

  const getDownloadSelector = (): ReactElement => {
    return (
      <div>
        <BaseNavOption
          className={isDownloading() && !isEditorMobileVariant() ? styles.width : ''}
          buttonChildren={getProgress()}
          id="nav-download-option"
          icon="icon-cloud-download"
          text={isDownloading() ? `${Math.round(currentDownloadData.displayProgress ?? 0)}%` : window.i18next.t('pmwjs_download')}
          optionType={downloadOptionType}
          iconType={IconType.DARK_SECONDARY}
          buttonType={Type.DARK_SECONDARY}
          hasMarginRight
          isLoading={!isInitialized}
          onClickHandler={(): void => {
            if (!isPremiumUser()) {
              onOpenDownloadModal();
            } else {
              if (isEditorMobileVariant()) {
                openNewDownloadModal({
                  id: 'mobile-download-panel',
                  posterObject: posterObject,
                  isOpenedInEditor: true,
                  isOpenedInModal: true,
                  isOpenedInPrintShop: false,
                });
                return;
              }
              trackPosterBuilderGA4Events(GA4EventName.NEW_DOWNLOAD_DIALOG_OPEN, {
                [GA4EventParamName.SOURCE]: 'editor',
              });
            }
          }}
        />
      </div>
    );
  };

  if (isPremiumUser()) {
    if (isEditorMobileVariant()) {
      return getDownloadSelector();
    }

    const getPopup = (): ReactElement => {
      return <DownloadPanel id="editor-download-panel" posterObject={posterObject} />;
    };

    return (
      <Dropdown
        onOpen={(): void => {
          void window.posterEditor?.whiteboard?.savePoster.save();
        }}
        popUpHasCustomWidth
        maxHeight={500}
        popupSpacingClass="spacing-p-0"
        topOffset={-5}
        leftOffset={-10}
        doCloseOnInnerClick={false}
        position={DROPDOWN_POSITION.FIXED}
        popupClassName={`${styles.popup} _overflow-hidden`}
        selector={getDownloadSelector()}
        popup={getPopup()}
        alignment={ALIGNMENT_TYPE.BOTTOM_END}
        isResumable
        doesCloseOnOuterClick={!isFileDropdownOpen && !isSliderBeingDragged}
      />
    );
  }

  return getDownloadSelector();
}
